import { Controller } from "@hotwired/stimulus"
import { escapeHTML } from "../helpers"

const TRIGGER_SELECTOR = "[data-tooltip], [data-tooltips] a[title], [data-popover]"
const EVENTS = ["pointerover", "pointerout", "focusin", "focusout"]

export default class extends Controller {
  active = new Map()

  connect() {
    this.toggleListeners("add")
  }

  disconnect() {
    this.toggleListeners("remove")
    for (const trigger of [...this.active.keys()]) this.hide(trigger)
  }

  toggleListeners(action) {
    for (const type of EVENTS) {
      this.element[`${action}EventListener`](type, this, true)
    }
  }

  handleEvent(event) {
    const trigger = this.findTrigger(event.target)
    if (!trigger) return
    if (event.type === "pointerover") {
      if (event.pointerType === "mouse") this.show(trigger)
    } else if (event.type === "focusin") {
      if (event.target.matches(":focus-visible")) this.show(trigger)
    } else if (!trigger.contains(event.relatedTarget)) {
      this.hide(trigger)
    }
  }

  findTrigger(node) {
    return node?.closest?.(TRIGGER_SELECTOR)
  }

  show(trigger) {
    if (this.active.has(trigger)) return
    if (!this.shouldShow(trigger)) return

    if (trigger.hasAttribute("data-popover")) {
      this.showPopover(trigger)
    } else {
      this.showTooltip(trigger)
    }
  }

  showPopover(trigger) {
    const tip = trigger.querySelector("[data-popover-content]")
    if (!tip) return

    const id = `tooltip-${Math.random().toString(16).slice(2, 10)}`
    Object.assign(tip, { popover: "manual", id })
    this.hideNativeTitle(trigger)
    trigger.setAttribute("aria-describedby", id)

    tip.showPopover({ source: trigger })
    this.active.set(trigger, { tip })
  }

  showTooltip(trigger) {
    const id = `tooltip-${Math.random().toString(16).slice(2, 10)}`
    const tip = this.createTip(this.textFor(trigger), id)
    Object.assign(tip, { popover: "manual", id })

    const anchorName = `--${id}`
    tip.dataset.placement = this.placementFor(trigger)
    tip.style.setProperty("position-anchor", anchorName)
    trigger.style.setProperty("anchor-name", anchorName)

    this.hideNativeTitle(trigger)
    trigger.setAttribute("aria-describedby", id)

    document.body.append(tip)
    tip.showPopover()

    this.active.set(trigger, {
      tip,
      cleanup: () => {
        tip.remove()
        trigger.style.removeProperty("anchor-name")
      },
    })
  }

  hide(trigger) {
    const state = this.active.get(trigger)
    if (!state) return

    if (state.tip.matches(":popover-open")) state.tip.hidePopover()
    state.cleanup?.()

    this.restoreNativeTitle(trigger)
    trigger.removeAttribute("aria-describedby")
    this.active.delete(trigger)
  }

  // Blank the title rather than remove it: relative-time treats a removed title as leave to write its own back.
  hideNativeTitle(trigger) {
    const title = trigger.getAttribute("title")
    if (!title) return
    trigger.setAttribute("aria-label", title)
    trigger.setAttribute("title", "")
  }

  restoreNativeTitle(trigger) {
    const title = trigger.getAttribute("aria-label")
    if (!title) return
    trigger.removeAttribute("aria-label")
    trigger.setAttribute("title", title)
  }

  shouldShow(trigger) {
    if (trigger.hasAttribute("data-tooltip-if-truncated")) {
      return trigger.scrollWidth > trigger.clientWidth
    }
    return true
  }

  placementFor(trigger) {
    const value = trigger.getAttribute("data-tooltip")
    if (value && value !== "true" && value !== "") return value
    return "top"
  }

  textFor(trigger) {
    return trigger.getAttribute("title") || trigger.getAttribute("aria-label") || ""
  }

  createTip(text, id) {
    const template = document.createElement("template")
    template.innerHTML = `<div class="tooltip" role="tooltip" id="${id}">${escapeHTML(text)}</div>`
    return template.content.firstElementChild
  }
}
